import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { getAuth } from "firebase/auth";
import { getAuthCentral } from "../../config/FirebaseConfig";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { checkDB } from "../../models/DataFunctions.js";
import JSZip from "jszip"; // For ZIP extraction
import addLogDocument from "../addLogDocument"; // Custom log function
import { getOrRefreshFirebaseToken } from "../../config/getFirebaseToken"; // Token utility
import { Box } from "@mui/material";
import Spinner from "../../loading/Spinner";
import { set } from "date-fns";
import { se } from "date-fns/locale";
import SmallSpinner from "../../loading/SmallSpinner";
import Icon1 from "../../icons/Icon";
import { getDocDataByRef, getFlutterTime } from "../../models/DataFunctions";
import { translateDocumentId } from "../../pages/folder_pages/FolderV2";
import createDocApi from "../database/createDocApi";
import { addActivityLog } from "../../config/OnlineStatus";

const UploadThermal = ({ folder, onUpdate, prefix }) => {
  const [files, setFiles] = useState([]);
  const [isHovered, setIsHovered] = useState(false); // To track hover state
  const [loading, setLoading] = useState(false);

  const onDropAccepted = useCallback(
    async (acceptedFiles) => {
      try {
        setLoading(true);
        const token = await getOrRefreshFirebaseToken(); // Use token utility to get or refresh the token

        const auth = getAuthCentral();
        const currentUser = auth.currentUser;

        if (!currentUser) {
          console.error("User not authenticated");
          return;
        }

        const db = getFirestore();
        const userRef = doc(db, "user", currentUser.uid);
        const userSnapshot = await getDoc(checkDB(userRef));

        if (!userSnapshot.exists()) {
          console.error("User document not found");
          return;
        }

        const org_id = userSnapshot.get("org_id");
        let repo_name = await translateDocumentId("thermal");

        await addActivityLog(
          "Documents déposés " + repo_name,
          folder.ref,
          org_id,
          userRef.id,
          "file_drop",
          prefix
        );

        // Append new files to the existing list
        const newFiles = [...files, ...acceptedFiles];
        setFiles(newFiles);

        for (const file of acceptedFiles) {
          let fileBytes = await file.arrayBuffer(); // Read file as bytes
          let extractedXml;

          if (file.type === "application/zip") {
            extractedXml = await extractXmlFromZip(fileBytes);
            if (extractedXml) {
              fileBytes = new TextEncoder().encode(extractedXml);
            } else {
              continue; // Skip if no XML found in the ZIP
            }
          }

          const url = prefix + `folders/${folder.id}/thermal`;
          const formData = new FormData();
          formData.append("file", new Blob([fileBytes]), file.name);

          // Set request headers
          const headers = {
            Authorization: `Bearer ${token}`,
          };
          let start = new Date();

          // Send the request
          const response = await fetch(url, {
            method: "POST",
            headers: headers,
            body: formData,
          });

          // Read response text once
          const responseText = await response.text();
          let end = new Date();
          let elapsed = end - start;
          // Log the upload
          await addLogDocument(
            url,
            prefix,
            userRef,
            org_id,
            response.status.toString(),
            folder.id,
            "",
            "", // request body as string
            responseText,
            "POST",
            elapsed
          );
          setLoading(false);
          if (response.ok) {
          } else {
            console.error(
              `Error response for file ${file.name}: ${response.status}`
            );
          }
        }

        // Trigger the onUpdate callback after all files are uploaded
        onUpdate();
      } catch (error) {
        console.error("Error uploading files:", error);
      }
    },
    [files, folder, onUpdate]
  );

  // Function to extract XML from a ZIP file
  const extractXmlFromZip = async (zipBytes) => {
    try {
      const zip = await JSZip.loadAsync(zipBytes);
      for (const filename in zip.files) {
        if (filename.endsWith(".xml")) {
          const fileData = await zip.file(filename).async("string");
          return new TextEncoder().encode(fileData); // Convert XML to byte array
        }
      }
      return null;
    } catch (e) {
      console.error("Error extracting XML from ZIP:", e);
      return null;
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropAccepted,
    accept: {
      "application/pdf": [], // PDF files
      "application/xml": [], // XML files
      "application/zip": [], // ZIP files
    },
    maxFiles: Infinity,
    maxSize: 52428800,
    multiple: true, // Allow multiple file uploads
  });

  const handleDelete = (fileToDelete) => {
    const updatedFiles = files.filter((file) => file !== fileToDelete);
    setFiles(updatedFiles);
  };

  return (
    <div className="flex flex-col gap-2 items-center w-full">
      <Box
        {...getRootProps()}
        onMouseEnter={() => setIsHovered(true)} // Trigger hover effect
        onMouseLeave={() => setIsHovered(false)} // Remove hover effect
        sx={{
          border: "1px dashed",
          borderColor: isDragActive ? "primary.main" : "grey.400",
          borderRadius: "8px",
          height: "30px",
          textAlign: "center",
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          padding: "0px 0px",
          width: "30px",
          cursor: "pointer",
          backgroundColor: loading
            ? "rgba(0, 0, 0, 0.1)"
            : isDragActive
              ? "rgba(0, 0, 255, 0.1)"
              : isHovered
                ? "rgba(0, 0, 0, 0.03)" // When hovered
                : "transparent", // Default background
        }}
      >
        <input {...getInputProps()} />
        {loading ? (
          <div className="text-h3 text-shadow_50">
            <SmallSpinner loading={true}></SmallSpinner>
          </div>
        ) : isDragActive ? (
          <Icon1 icon_name="join" size={15} fill="grey" />
        ) : (
          <Icon1 icon_name="join" size={15} fill="grey" />
        )}
      </Box>
    </div>
  );
};

export default UploadThermal;
